// Auto-dismissing Rails flash toasts.
//
// Scope: the layout flash only — `layouts/application.html.erb` renders `notice` as
// `.alert.alert-info.alert-block` and `alert` as `.alert.alert-danger.alert-block`. The selector is
// deliberately `.alert-block:not(.notifier-alert)`:
//   - `.notifier-alert` is notifier.js's creator toast; it owns its own lifecycle (autoHide timers
//     and the determinate `{{progress}}` bar) and must not be touched from here.
//   - the unconfirmed-email banner (`.alert.auth-bar`) carries no `.alert-block`, so it is already
//     outside the selector — it is a standing notice, not a toast, and must never vanish.
//
// Tones: success / notice / info auto-hide after AUTO_HIDE_MS with a countdown bar. Danger stays
// put and is dismissed by its close × (owner decision, 2026-08-21: a vanishing error is a support
// ticket). The close button itself is rendered server-side so its label can be translated.

// Unified with notifier.js's `autoHide` — one dismissal window across the app.
export const AUTO_HIDE_MS = 3000;

// Must match the `.alert-toast-out` transition in components/layout/_notifications.scss.
const EXIT_MS = 250;

const TOAST_SELECTOR = '.alert-block:not(.notifier-alert)';
const PERSISTENT_SELECTOR = '.alert-danger';

const dismiss = (node) => {
  if (!node.isConnected) return;

  node.classList.add('alert-toast-out');
  setTimeout(() => node.remove(), EXIT_MS);
};

// The bar is a pure CSS `scaleX` animation (linear, `transform-origin: left`) — no per-frame JS.
const buildCountdown = () => {
  const track = document.createElement('div');
  track.className = 'alert-countdown';
  track.setAttribute('aria-hidden', 'true');

  const bar = document.createElement('span');
  bar.className = 'alert-countdown-bar';
  bar.style.animationDuration = `${AUTO_HIDE_MS}ms`;
  track.appendChild(bar);

  return track;
};

const armAutoHide = (node) => {
  const track = buildCountdown();
  node.appendChild(track);

  let timer = setTimeout(() => dismiss(node), AUTO_HIDE_MS);

  // Hover pauses the bar and the timer; leaving restarts both from the top, so a toast the reader
  // was still on always gets a full window afterwards.
  node.addEventListener('mouseenter', () => {
    clearTimeout(timer);
    node.classList.add('alert-countdown-paused');
  });

  node.addEventListener('mouseleave', () => {
    node.classList.remove('alert-countdown-paused');

    const bar = track.querySelector('.alert-countdown-bar');
    if (bar) {
      // Re-insert the node to replay the animation from 0 (class toggling alone would not).
      const fresh = bar.cloneNode(true);
      bar.replaceWith(fresh);
    }

    clearTimeout(timer);
    timer = setTimeout(() => dismiss(node), AUTO_HIDE_MS);
  });
};

export const initFlashToasts = (root = document) => {
  root.querySelectorAll(TOAST_SELECTOR).forEach((node) => {
    const closeButton = node.querySelector('.close');
    if (closeButton) {
      closeButton.addEventListener('click', () => dismiss(node));
    }

    if (node.matches(PERSISTENT_SELECTOR)) return;

    armAutoHide(node);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initFlashToasts());
} else {
  initFlashToasts();
}
